/**
 * Live evaluation of the I-864 income threshold against the form
 * engine's current answers. Drives the auto-skip behavior on the
 * Sponsor Financials page: when the sponsor's household income
 * already clears 125% of the Federal Poverty Guideline for their
 * household size, Part 7 (assets) and the Part 6 household-co-filer
 * block are not required and should not be shown.
 *
 * Source of truth:
 *   - household income → `sponsor.income.household_total_annual`
 *     (the renderer's `useComputedTotalSync` keeps this current as
 *     `individual_annual + sum(household co-filer incomes)`)
 *   - household size → sum of the six `sponsor.household.*_count`
 *     fields (the catalog's `total_size` is calculated at PDF-render
 *     time and may not be present in the engine)
 *   - region → `sponsor.current_physical_address.state` (USPS code)
 */
import { useEffect, useState } from 'react';
import type { FieldId } from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';

import {
  compute125PovertyThreshold,
  getPovertyRegionForState,
  meetsPovertyThreshold,
  type TPovertyRegion,
} from './povertyGuidelines';

export interface IIncomeThresholdState {
  /** True when household income clears 125% × FPG for the household. */
  meets: boolean;
  /** The 125% poverty threshold in dollars. 0 when household size unknown. */
  threshold: number;
  /** Total household income (the catalog computed total). */
  householdIncome: number;
  /** Sponsor's individual annual income (Part 6 Item 7). */
  individualIncome: number;
  /** Effective household size — `Math.max(1, sum(counts))`. */
  householdSize: number;
  /** Resolved poverty region (alaska / hawaii / contiguous). */
  region: TPovertyRegion;
  /**
   * Why `meets` is what it is. Useful for analytics/copy decisions:
   *   - `no_income`: sponsor hasn't entered individual_annual yet
   *   - `unknown_household`: no household-size data yet (≤1)
   *   - `meets`: cleared the threshold
   *   - `falls_short`: income below threshold
   */
  reason: 'no_income' | 'unknown_household' | 'meets' | 'falls_short';
}

const HOUSEHOLD_COUNT_FIELDS: readonly FieldId[] = [
  'sponsor.household.yourself_count' as FieldId,
  'sponsor.household.spouse_as_immigrant_count' as FieldId,
  'sponsor.household.dependent_children_count' as FieldId,
  'sponsor.household.other_dependents_count' as FieldId,
  'sponsor.household.income_contributors_count' as FieldId,
  'sponsor.household.previously_sponsored_persons_count' as FieldId,
];

const INDIVIDUAL_INCOME_FIELD =
  'sponsor.income.individual_annual' as FieldId;
const HOUSEHOLD_TOTAL_INCOME_FIELD =
  'sponsor.income.household_total_annual' as FieldId;
const SPONSOR_STATE_FIELD =
  'sponsor.current_physical_address.state' as FieldId;

function readNumber(engine: FormEngine, fieldId: FieldId): number {
  const ans = engine.getAnswer(fieldId);
  if (ans?.type === 'number' && Number.isFinite(ans.value)) return ans.value;
  return 0;
}

function readString(engine: FormEngine, fieldId: FieldId): string {
  const ans = engine.getAnswer(fieldId);
  if (ans?.type === 'text' || ans?.type === 'select') return ans.value;
  return '';
}

function readHouseholdSize(engine: FormEngine): number {
  let size = 0;
  for (const id of HOUSEHOLD_COUNT_FIELDS) {
    size += readNumber(engine, id);
  }
  // Marriage-based AOS floor: 2. The catalog documents `yourself_count`
  // and `spouse_as_immigrant_count` as "Always 1" for marriage-based
  // filings (this app's only supported path per the screener). If the
  // sponsor hasn't entered the household section yet, defaulting to 2
  // — not 1 — is the right call: a $20k sponsor would clear the
  // single-person threshold ($19,562) but is well short of the
  // marriage-household threshold ($26,437), and we must not silently
  // tell them they're cleared when they're not.
  return Math.max(2, size);
}

/**
 * Pure evaluation given a snapshot of engine reads. Tested in
 * isolation; the React hook below is the thin wrapper.
 */
export function evaluateIncomeThreshold(
  individualIncome: number,
  householdIncome: number,
  householdSize: number,
  region: TPovertyRegion,
): IIncomeThresholdState {
  const threshold = compute125PovertyThreshold(householdSize, region);
  // Use the higher of individual vs. household income — the form
  // accepts either Part 6 Item 12 OR Item 16 against the guidelines
  // (see the I-864 instruction line: "If your income, or the total
  // income for you and your household, from Part 6., Item Numbers
  // 12. or 16. exceeds…").
  const effectiveIncome = Math.max(individualIncome, householdIncome);
  if (effectiveIncome <= 0) {
    return {
      meets: false,
      threshold,
      householdIncome,
      individualIncome,
      householdSize,
      region,
      reason: 'no_income',
    };
  }
  if (threshold <= 0) {
    return {
      meets: false,
      threshold,
      householdIncome,
      individualIncome,
      householdSize,
      region,
      reason: 'unknown_household',
    };
  }
  const meets = meetsPovertyThreshold(effectiveIncome, householdSize, region);
  return {
    meets,
    threshold,
    householdIncome,
    individualIncome,
    householdSize,
    region,
    reason: meets ? 'meets' : 'falls_short',
  };
}

/**
 * React hook: subscribes to engine revisions and returns the live
 * threshold state. Used by SectionRenderer to decide whether to
 * auto-hide the asset block and co-filer gate.
 */
export function useIncomeThreshold(engine: FormEngine): IIncomeThresholdState {
  const compute = (): IIncomeThresholdState => {
    const individualIncome = readNumber(engine, INDIVIDUAL_INCOME_FIELD);
    const householdIncome = readNumber(engine, HOUSEHOLD_TOTAL_INCOME_FIELD);
    const householdSize = readHouseholdSize(engine);
    const region = getPovertyRegionForState(
      readString(engine, SPONSOR_STATE_FIELD) || undefined,
    );
    return evaluateIncomeThreshold(
      individualIncome,
      householdIncome,
      householdSize,
      region,
    );
  };
  const [state, setState] = useState<IIncomeThresholdState>(compute);
  useEffect(() => engine.subscribe(() => setState(compute())), [engine]);
  return state;
}
